import React from "react";
import { motion } from "framer-motion";
import { FaHammer, FaShieldAlt, FaGem, FaCheckCircle } from "react-icons/fa";

export default function Craftsmanship() {
  const features = [
    { 
      icon: <FaHammer />, 
      title: "Hand-Finished Precision", 
      text: "Every curve and joint is inspected by master craftsmen." 
    },
    { 
      icon: <FaShieldAlt />, 
      title: "Unmatched Strength", 
      text: "Reinforced structures designed to last for generations." 
    },
    { 
      icon: <FaGem />, 
      title: "Premium Materials", 
      text: "Only the finest teak and mahogany woods are selected." 
    },
  ];

  return (
    // Reduced padding from py-5 to py-4 to make it smaller
    <section className="py-4" style={{ background: "#000000", overflow: "hidden" }}>
      <div className="container py-lg-4">
        <div className="row align-items-center g-4 g-lg-5">
          
          {/* IMAGE SIDE */}
          <div className="col-lg-5 col-md-8 mx-auto order-1">
            <div style={{ position: "relative", padding: "10px" }}>
              
              <div className="d-none d-md-block" style={{
                position: "absolute",
                top: "10%",
                left: "-10%",
                width: "100%",
                height: "80%",
                background: "rgba(249, 115, 22, 0.15)",
                borderRadius: "40px",
                filter: "blur(60px)",
                zIndex: 0
              }} />

              <motion.div
  initial={{ opacity: 0, scale: 0.9 }}
  whileInView={{ opacity: 1, scale: 1 }}
  viewport={{ once: true }}
  transition={{ duration: 0.8 }}
  style={{
    position: "relative",
    zIndex: 2,
    borderRadius: "24px",
    overflow: "hidden",
    maxHeight: "520px",
    width: "420px",          // add width
    maxWidth: "80%",        // responsive
    margin: "0 auto",        // center horizontally
    background: "#f2f1eb",
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
    textAlign: "center",
    padding: "15px"
  }}
>
                {/* NEW DOOR ANIMATION: Floating & Subtle Scale */}
                <motion.img
                  animate={{ 
                    y: [0, -12, 0],
                    rotateY: [0, 5, 0] // Subtle 3D perspective shift
                  }}
                  transition={{ 
                    duration: 5, 
                    repeat: Infinity, 
                    ease: "easeInOut" 
                  }}
                  src="https://res.cloudinary.com/dtuqzt18e/image/upload/v1779694300/VL-404_trymai.png"
                  alt="Craftsmanship"
                  style={{ 
                    width: "auto", 
                    maxHeight: "480px", // Reduced from 600px
                    maxWidth: "100%", 
                    objectFit: "contain",
                    filter: "drop-shadow(10px 10px 20px rgba(0,0,0,0.15))"
                  }}
                />
                
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  style={{
                    position: "absolute",
                    top: "15px",
                    right: "15px",
                    background: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(5px)",
                    padding: "6px 12px",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
                    border: "1px solid #eee"
                  }}
                >
                  <FaCheckCircle color="#16A34A" size={12} />
                  <span style={{ fontWeight: "bold", fontSize: "0.75rem", color: "#333" }}>Premium Quality</span>
                </motion.div>
              </motion.div>

              <div style={{
                position: "absolute",
                bottom: "-10px",
                right: "-10px",
                width: "100px",
                height: "100px",
                border: "5px solid #F97316",
                borderRadius: "15px",
                zIndex: 1
              }} />
            </div>
          </div>

          {/* TEXT SIDE */}
          <div className="col-lg-7 order-2 mt-4 mt-lg-0">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span style={{ 
                color: "#F97316", 
                fontWeight: "800", 
                textTransform: "uppercase", 
                letterSpacing: "3px", 
                fontSize: "0.8rem" 
              }}>
                Artisan Quality
              </span>
              
              <h2 style={{ 
                fontSize: "calc(1.5rem + 1.2vw)", // Slightly smaller title
                fontWeight: "800", 
                color: "#FFFFFF", 
                margin: "10px 0 20px 0",
                lineHeight: "1.2"
              }}>
                The Art of <span style={{ color: "#F97316" }}>Perfect</span> Engineering
              </h2>
              
              <p style={{ fontSize: "1rem", lineHeight: "1.7", marginBottom: "30px", color: "#A1A1AA" }}>
                We believe a door is more than just an entry—it’s a masterpiece of security and style. 
                Our process combines traditional woodworking with modern technology.
              </p>

              <div className="row g-3">
                {features.map((item, index) => (
                  <div className="col-md-12" key={index}>
                    <motion.div 
                      whileHover={{ x: 8, background: "#111111" }}
                      className="d-flex align-items-center p-3"
                      style={{ 
                        background: "#0A0A0A", 
                        borderRadius: "12px", 
                        border: "1px solid #1A1A1A",
                        transition: "0.3s"
                      }}
                    >
                      <div style={{
                        width: "45px",
                        height: "45px",
                        background: "rgba(249, 115, 22, 0.1)",
                        color: "#F97316",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.1rem",
                        marginRight: "15px",
                        flexShrink: 0
                      }}>
                        {item.icon}
                      </div>
                      <div>
                        <h6 className="fw-bold mb-0" style={{ color: "#FFFFFF", fontSize: "0.95rem" }}>{item.title}</h6>
                        <p className="small mb-0" style={{ color: "#71717A", fontSize: "0.85rem" }}>{item.text}</p>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
}